/**
 * EditPanel - Panel de control para modo edición de coordenadas
 * V14-V16 - Interface de edición PTEL
 * 
 * Muestra toggle de edición, lista de cambios pendientes,
 * y botones para confirmar/descartar.
 */

import React, { memo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Pencil, Check, X, Undo2, MapPin, AlertTriangle } from 'lucide-react';
import type { PendingEdit, EditModeState } from './useEditMode';
import type { AddressGeocodedResult } from './addressSearch';
import { AddressSearchInput } from './AddressSearchInput';
import { formatUTM, distanceUTM, azimuthUTM, cardinalLabel } from '../../../lib/leaflet-crs';
import { useAudit } from '../../../hooks/useAudit';
import './EditPanel.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface EditPanelProps {
  /** Estado actual del modo edición */
  state: EditModeState;
  /** Toggle modo edición */
  onToggleEdit: () => void;
  /** Confirmar todos los cambios */
  onConfirm: () => void;
  /** Descartar todos los cambios */
  onDiscard: () => void;
  /** Deshacer cambio específico */
  onUndoEdit: (infrastructureId: string) => void;
  /** Seleccionar infraestructura */
  onSelectEdit: (infrastructureId: string) => void;
  /** Callback cuando se geocodifica una dirección */
  onAddressGeocoded?: (result: AddressGeocodedResult) => void;
  /** Municipio para acotar búsquedas */
  municipality?: string;
  /** Código INE5 del municipio (para auditoría de coordenadas) */
  municipalityINE?: string;
  /** Posición del panel */
  position?: 'left' | 'right';
  /** Clases CSS adicionales */
  className?: string;
}

// =============================================================================
// SUBCOMPONENTES
// =============================================================================

interface EditItemProps {
  edit: PendingEdit;
  isSelected: boolean;
  onSelect: () => void;
  onUndo: () => void;
}

const EditItem = memo<EditItemProps>(function EditItem({
  edit,
  isSelected,
  onSelect,
  onUndo,
}) {
  // Calcular distancia y azimut del cambio
  const distance = distanceUTM(
    edit.originalCoords.x,
    edit.originalCoords.y,
    edit.newCoords.x,
    edit.newCoords.y
  );
  
  // Formatear distancia
  const distanceStr = distance < 1000
    ? `${Math.round(distance)} m`
    : `${(distance / 1000).toFixed(2)} km`;

  // Azimut y cardinal
  const az = azimuthUTM(
    edit.originalCoords.x,
    edit.originalCoords.y,
    edit.newCoords.x,
    edit.newCoords.y
  );
  const cardinal = distance >= 0.5 ? cardinalLabel(az) : '';
  const azStr = distance >= 0.5 ? `${cardinal} (${Math.round(az)}°)` : '';

  return (
    <div
      className={`ptel-edit-item ${isSelected ? 'ptel-edit-item--selected' : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
    >
      <div className="ptel-edit-item__header">
        <MapPin className="ptel-edit-item__icon" size={14} />
        <span className="ptel-edit-item__name" title={edit.infrastructureName}>
          {edit.infrastructureName.length > 25 
            ? edit.infrastructureName.slice(0, 25) + '…' 
            : edit.infrastructureName}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="ptel-edit-item__undo"
          onClick={(e) => {
            e.stopPropagation();
            onUndo();
          }}
          title="Deshacer este cambio"
        >
          <Undo2 size={12} />
        </Button>
      </div>
      
      <div className="ptel-edit-item__coords">
        <div className="ptel-edit-item__coord ptel-edit-item__coord--old">
          <span className="ptel-edit-item__label">Antes:</span>
          <span className="ptel-edit-item__value">
            {formatUTM(edit.originalCoords.x, edit.originalCoords.y, 0)}
          </span>
        </div>
        <div className="ptel-edit-item__coord ptel-edit-item__coord--new">
          <span className="ptel-edit-item__label">Después:</span>
          <span className="ptel-edit-item__value">
            {formatUTM(edit.newCoords.x, edit.newCoords.y, 0)}
          </span>
        </div>
      </div>
      
      <div className="ptel-edit-item__meta">
        <span className="ptel-edit-item__distance" title="Distancia y dirección del movimiento">
          Δ {distanceStr}{azStr ? ` · ${azStr}` : ''}
        </span>
        <span className="ptel-edit-item__source">
          {edit.source === 'drag' ? '🖱️ Arrastrado' : '📍 Click'}
        </span>
      </div>
    </div>
  );
});

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export const EditPanel = memo<EditPanelProps>(function EditPanel({
  state,
  onToggleEdit,
  onConfirm,
  onDiscard,
  onUndoEdit,
  onSelectEdit,
  onAddressGeocoded,
  municipality,
  municipalityINE,
  position = 'right',
  className = '',
}) {
  const { isActive, selectedId, pendingEdits, hasUnsavedChanges } = state;
  const { isAtLimit, remainingSlots, maxEdits } = state;

  const { logCoordEdit } = useAudit();

  // Handler envuelto: confirma primero y luego registra en auditoría
  const handleConfirm = useCallback(async () => {
    onConfirm();
    if (municipalityINE) {
      for (const edit of pendingEdits) {
        const source = edit.source === 'drag' ? 'drag' : 'manual';
        void logCoordEdit(
          edit.infrastructureId,
          edit.infrastructureName,
          municipalityINE,
          { x: edit.originalCoords.x, y: edit.originalCoords.y },
          { x: edit.newCoords.x, y: edit.newCoords.y },
          source,
        );
      }
    }
  }, [onConfirm, pendingEdits, municipalityINE, logCoordEdit]);

  // Handler para seleccionar
  const handleSelect = useCallback((id: string) => {
    onSelectEdit(id);
  }, [onSelectEdit]);

  // Si no está activo, mostrar solo botón de activar
  if (!isActive) {
    return (
      <Button
        variant="outline"
        onClick={onToggleEdit}
        className={`
          ptel-edit-toggle
          fixed top-36 z-[1000]
          ${position === 'left' ? 'left-4' : 'right-4'}
          bg-stone-900 border-stone-700 hover:bg-stone-800
          text-stone-200 hover:text-white
          ${className}
        `}
        title="Activar modo edición de coordenadas"
      >
        <Pencil className="h-4 w-4 mr-2" />
        Editar coordenadas
      </Button>
    );
  }

  return (
    <Card className={`
      ptel-edit-panel
      fixed top-36 z-[1000] w-80 max-h-[calc(100vh-10rem)]
      ${position === 'left' ? 'left-4' : 'right-4'}
      bg-stone-900 border-amber-600/50
      ${className}
    `}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium text-amber-400 flex items-center gap-2">
          <Pencil className="h-4 w-4" />
          Modo Edición
          {hasUnsavedChanges && (
            <span className="ml-auto text-xs bg-amber-600 text-stone-900 px-2 py-0.5 rounded">
              {pendingEdits.length} cambio{pendingEdits.length !== 1 ? 's' : ''}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Instrucciones */}
        <div className="text-xs text-stone-400 space-y-1">
          <p>🖱️ <strong>Arrastra</strong> los marcadores para moverlos</p>
          <p>📍 <strong>Click</strong> en el mapa para posicionar</p>
        </div>

        {/* Buscador de direcciones */}
        {onAddressGeocoded && (
          <AddressSearchInput
            onAddressGeocoded={onAddressGeocoded}
            municipality={municipality}
            placeholder="Buscar dirección…"
          />
        )}

        {/* Lista de cambios pendientes */}
        {pendingEdits.length > 0 ? (
          <div className="ptel-edit-list space-y-2 max-h-48 overflow-y-auto">
            {pendingEdits.map((edit) => (
              <EditItem
                key={edit.infrastructureId}
                edit={edit}
                isSelected={selectedId === edit.infrastructureId}
                onSelect={() => handleSelect(edit.infrastructureId)}
                onUndo={() => onUndoEdit(edit.infrastructureId)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-stone-500 text-sm">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Sin cambios pendientes</p>
            <p className="text-xs">Arrastra un marcador para editarlo</p>
          </div>
        )}

        {/* Aviso de límite de ediciones */}
        {remainingSlots <= 5 && remainingSlots > 0 && pendingEdits.length > 0 && (
          <div className="flex items-start gap-2 p-2 bg-amber-900/30 rounded text-xs text-amber-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Quedan <strong>{remainingSlots}</strong> de {maxEdits} ediciones.
              Confirma los cambios actuales para continuar editando.
            </span>
          </div>
        )}
        {isAtLimit && (
          <div className="flex items-start gap-2 p-2 bg-red-900/40 rounded text-xs text-red-300 border border-red-700/50">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Límite de <strong>{maxEdits}</strong> ediciones alcanzado.
              Confirma o descarta antes de seguir editando.
            </span>
          </div>
        )}

        {/* Advertencia de cambios grandes */}
        {pendingEdits.some(e => 
          distanceUTM(e.originalCoords.x, e.originalCoords.y, e.newCoords.x, e.newCoords.y) > 1000
        ) && (
          <div className="flex items-start gap-2 p-2 bg-amber-900/30 rounded text-xs text-amber-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Algunos cambios son mayores a 1 km. Verifica que sean correctos.</span>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex gap-2 pt-2 border-t border-stone-700">
          <Button
            variant="outline"
            size="sm"
            onClick={onDiscard}
            className="flex-1 text-stone-400 hover:text-stone-200 border-stone-600"
            disabled={!hasUnsavedChanges}
          >
            <X className="h-4 w-4 mr-1" />
            Descartar
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleConfirm}
            className="flex-1 bg-lime-600 hover:bg-lime-500 text-stone-900"
            disabled={!hasUnsavedChanges}
          >
            <Check className="h-4 w-4 mr-1" />
            Confirmar
          </Button>
        </div>

        {/* Botón salir */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleEdit}
          className="w-full text-stone-500 hover:text-stone-300"
        >
          Salir del modo edición
        </Button>
      </CardContent>
    </Card>
  );
});

export default EditPanel;
